import { useState, useEffect, useContext } from "react";
import { PropTypes } from "prop-types";

import { HiddenElementsContext } from "../../context/HiddenElements";
import { withALErrorBoundary } from "../../helpers/ErrorBoundary/ALErrorBoundary";
import { ComponentType } from "../../constants/ComponentType";
import { Country } from "../../constants/Country";
import { HandleType } from "../../constants/HandleType";

import PriceContainer from "../PriceContainer";
import AddToCart from "../ProductPage/productSummary/AddToCart";

import "./floatingAddToCart/styles.scss";

const FLOATING_ATC_SCROLL_PIXEL_THRESHOLD = 560;

function FloatingAddToCart({
  product,
  selectedVariant,
  variantSelectedOptions,
  scrollToVariants,
  countryCode,
  searchObject,
  prices,
  componentType,
  optionalProducts = [],
  setOptionalProducts,
  optionalProductsType,
}) {
  const [showButton, setShowButton] = useState(false);
  const { hide, show } = useContext(HiddenElementsContext);

  const showAddToCart =
    countryCode === Country.US ||
    (countryCode !== Country.US && product?.handle !== HandleType.GIFT_CARD);

  useEffect(() => {
    const handleScroll = () => {
      const isBellowThreshold = window.pageYOffset > FLOATING_ATC_SCROLL_PIXEL_THRESHOLD;
      setShowButton(isBellowThreshold);
      if (componentType === ComponentType.PDP_ADS) {
        // Always hide chat for PDP_ADS
        hide();
      } else if (isBellowThreshold) {
        hide();
      } else {
        show();
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      show();
    };
  }, [hide, show]);

  if (!showButton) {
    return null;
  }

  const optionalProductsTotalPrice = optionalProducts?.reduce(
    (acc, product) => acc + Number(product.prices?.finalPrice || 0),
    0
  );

  return (
    <div className="floating_add_to_cart">
      <div className="floating_add_to_cart__details">
        <p className="subtext subtext--bold subtext--uc">
          {product?.title.split(" - ")[1] || product?.title}
          {optionalProductsType ? ` + ${optionalProductsType}` : ""}
        </p>

        <PriceContainer
          productId={product.id}
          selectedVariant={selectedVariant}
          prices={prices}
          optionalProductsTotalPrice={optionalProductsTotalPrice}
          componentType={
            componentType === ComponentType.PDP_ADS
              ? ComponentType.FLOATING_ATC_ADS
              : ComponentType.FLOATING_ATC
          }
          tags={product?.tags}
        />
      </div>

      {showAddToCart && (
        <AddToCart
          product={product}
          variantSelectedOptions={variantSelectedOptions}
          searchObject={searchObject}
          fallBackFunction={selectedVariant.title !== "Default Title" ? scrollToVariants : null}
          prices={prices}
          componentType={ComponentType.FLOATING_ATC}
          additionalProducts={optionalProducts.map((p) => ({
            product: p,
            prices: p.prices,
          }))}
          addProductCallback={() => {
            if (optionalProducts.length > 0) {
              setOptionalProducts([]);
            }
          }}
        />
      )}
    </div>
  );
}

FloatingAddToCart.propTypes = {
  scrollToVariants: PropTypes.func,
  product: PropTypes.shape({
    title: PropTypes.string,
    handle: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    variants: PropTypes.arrayOf(
      PropTypes.shape({ sku: PropTypes.string, title: PropTypes.string })
    ),
  }),
  selectedVariant: PropTypes.shape({
    title: PropTypes.string,
  }),
  variantSelectedOptions: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
  }),
  countryCode: PropTypes.string,
  searchObject: PropTypes.shape({
    isSearch: PropTypes.bool,
    index: PropTypes.string,
    queryID: PropTypes.string,
    objectID: PropTypes.string,
  }),
  prices: PropTypes.shape({
    full_price: PropTypes.string,
    final_price: PropTypes.string,
    discount_given: PropTypes.string,
  }),
  componentType: PropTypes.string,
  optionalProducts: PropTypes.array,
  setOptionalProducts: PropTypes.func,
  optionalProductsType: PropTypes.string,
};

export default withALErrorBoundary({
  name: "FloatingAddToCart",
  priority: "P1",
})(FloatingAddToCart);
